import { Badge, Button, Callout, Skeleton } from '@provenance/design';
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import type { SyncState } from './useDraftProof';

/**
 * The authoritative render, beside the editor.
 *
 * The output of **the same engine that stamps real certificates**, rendered from the
 * layout exactly as it is on screen — saved or not — shortly after each change (T-126).
 *
 * It also says whether the editor is telling the truth. The engine reports where it drew
 * every mark; if any differs from the box the editor drew by more than half a point, this
 * names it. With the editor and engine sharing one box calculation (T-125) that should
 * never appear — it is a tripwire, and it is worded as our fault, because it would be.
 */
export function ProofPane({
  url,
  loading,
  error,
  current,
  sync,
  onRender,
}: {
  url: string | null;
  loading: boolean;
  error: string | null;
  /** False while the layout has changed since this proof was rendered. */
  current: boolean;
  sync: SyncState;
  onRender: () => void;
}) {
  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-[14ch] flex-1 [font-size:12.5px] text-[var(--pv-muted)]">
          Rendered by the engine that stamps real certificates, from the layout on screen.
        </p>
        {url && (
          <Button variant="ghost" size="sm" asChild leadingIcon={<ExternalLink className="size-3.5" />}>
            <a href={url} target="_blank" rel="noreferrer">
              Open
            </a>
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          loading={loading}
          leadingIcon={<RefreshCw className="size-3.5" />}
          onClick={onRender}
        >
          Re-render
        </Button>
      </div>

      <div role="status" aria-live="polite" className="flex min-h-6 items-center">
        {!current || loading ? (
          <span className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-muted)]">
            <Loader2 aria-hidden className="size-3 animate-spin" /> Updating the proof…
          </span>
        ) : sync.kind === 'in-sync' ? (
          <Badge tone="ok" size="sm" dot>
            In sync · {sync.checked} mark{sync.checked === 1 ? '' : 's'} within 0.5pt
          </Badge>
        ) : null}
      </div>

      {current && !loading && sync.kind === 'out-of-sync' && (
        <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title={`Out of sync: ${sync.label}`}>
          The editor shows it at {fmt(sync.editor.width)} × {fmt(sync.editor.height)} pt, but the
          proof prints {fmt(sync.proof.width)} × {fmt(sync.proof.height)} pt
          {Math.abs(sync.editor.x - sync.proof.x) > 0.5 || Math.abs(sync.editor.y - sync.proof.y) > 0.5
            ? `, and at ${fmt(sync.proof.x)}, ${fmt(sync.proof.y)} rather than ${fmt(sync.editor.x)}, ${fmt(sync.editor.y)}`
            : ''}
          . That is a fault on our side, not in your layout — trust the proof, it is what prints.
        </Callout>
      )}

      {error ? (
        <Callout tone="warn" icon={<AlertTriangle className="size-4" />} title="Could not render the proof">
          {error}
        </Callout>
      ) : !url ? (
        <Skeleton className="min-h-[420px] flex-1" />
      ) : (
        <iframe
          key={url}
          src={`${url}#toolbar=0&navpanes=0`}
          title="Stamping proof"
          className="min-h-[420px] w-full flex-1 rounded-[var(--pv-radius)] border border-[var(--pv-line-strong)] bg-[var(--pv-card)]"
        />
      )}

      {current && !loading && sync.kind === 'in-sync' && (
        <p className="flex items-center gap-1.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
          <CheckCircle2 aria-hidden className="size-3 text-[var(--pv-ok)]" />
          Every mark is exactly where the editor shows it.
        </p>
      )}
    </div>
  );
}

function fmt(value: number): string {
  return (Math.round(value * 10) / 10).toString();
}
